import taskEndpoints from '@/lib/api/endpoints/taskEndpoints';
import type { Task } from '@/lib/models/task';
import { TaskSchema } from '@/lib/models/task';
import type { TaskIntegration } from '@/lib/models/taskIntegration';
import { TaskIntegrationSchema } from '@/lib/models/taskIntegration';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';

/**
 * Interfaccia per il service dei Tasks
 * Wrapper che estrae automaticamente i dati dalle response Axios
 */
export interface TasksService {
	/**
	 * Recupera la lista dei task con query OData
	 * @param query - Query string OData (es: "?$top=25&$count=true&$orderby=description asc")
	 * @returns Promise con response OData contenente array di Task
	 */
	getTasks(query: string): Promise<ODataResponse<Task>>;

	/**
	 * Recupera un singolo task per ID
	 * @param id - ID del task
	 * @returns Promise con singolo Task
	 */
	getTask(id: number): Promise<Task>;

	/**
	 * Aggiorna un task esistente
	 * @param id - ID del task da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con Task aggiornato
	 */
	updateTask(id: number, payload: Task): Promise<Task>;

	/**
	 * Crea un nuovo task
	 * @param payload - Dati del nuovo task
	 * @returns Promise con Task creato
	 */
	postTask(payload: Task): Promise<Task>;

	/**
	 * Elimina un task
	 * @param id - ID del task da eliminare
	 * @returns Promise void
	 */
	deleteTask(id: number): Promise<void>;

	/**
	 * Recupera le integrazioni per un task
	 * @param taskId - ID del task
	 * @returns Promise con array di TaskIntegration (compatibilità con JS originale)
	 */
	getIntegrations(taskId: number): Promise<TaskIntegration[]>;
}

/**
 * Implementazione del service per i Tasks
 * Estrae automaticamente la proprietà .data dalle response Axios
 * Uniformato con la struttura di activitiesService
 */
const tasksService: TasksService = {
	async getTasks(query: string): Promise<ODataResponse<Task>> {
		const response = (await taskEndpoints.getTasks(query)).data;
		// Validazione OData response con schema Task
		zodValidator.validateODataResponse(response, TaskSchema, 'Task.getAll');
		return response;
	},

	async getTask(id: number): Promise<Task> {
		const rawData = (await taskEndpoints.getTask(id)).data;
		// Validazione runtime della response
		zodValidator.validateSingle(rawData, TaskSchema, `Task.getById(${id})`);
		return rawData;
	},

	async updateTask(id: number, payload: Task): Promise<Task> {
		const response = (await taskEndpoints.updateTask(id, payload)).data;
		// Validazione runtime della response
		zodValidator.validateSingle(response, TaskSchema, 'Task.update');
		return response;
	},

	async postTask(payload: Task): Promise<Task> {
		const response = (await taskEndpoints.postTask(payload)).data;
		// Validazione runtime della response
		zodValidator.validateSingle(response, TaskSchema, 'Task.create');
		return response;
	},

	async deleteTask(id: number): Promise<void> {
		return (await taskEndpoints.deleteTask(id)).data;
	},

	async getIntegrations(taskId: number): Promise<TaskIntegration[]> {
		const { data } = await taskEndpoints.getIntegrations(taskId);
		// Validazione runtime centralizzata per le integrazioni (attiva solo in development)
		zodValidator.validateODataResponse(
			data,
			TaskIntegrationSchema,
			`Task.getIntegrations(${taskId})`,
		);
		return data.value;
	},
};

export default tasksService;
